import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CategoryIcon } from "../components/CategoryIcon";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const IMG = "https://images.pexels.com/photos/6646873/pexels-photo-6646873.jpeg";

export default function Submit() {
  const { t, catLabel } = useLang();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ name: "", short_description: "", services: "", phone: "", email: "", website: "", address: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (c) => setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.short_description) {
      toast.error("Please add a name and description.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/organizations/submit", {
        name: form.name,
        short_description: form.short_description,
        services: form.services.split("\n").map((s) => s.trim()).filter(Boolean),
        categories: selected,
        contact: { phone: form.phone, email: form.email, website: form.website, address: form.address },
      });
      toast.success(t("submit_ok"));
      navigate("/");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 fade-up">
      <div className="rounded-3xl overflow-hidden mb-8 relative">
        <img src={IMG} alt="Volunteers" className="w-full h-44 object-cover" />
        <div className="absolute inset-0 bg-[#2E604A]/70 flex flex-col justify-center px-6 sm:px-10">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{t("submit_title")}</h1>
          <p className="text-white/90 text-sm mt-2 max-w-lg">{t("submit_sub")}</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5 bg-white rounded-2xl border border-stone-200 shadow-sm p-6 sm:p-8">
        <Field label={t("f_name")}><Input data-testid="submit-name" value={form.name} onChange={set("name")} className="rounded-xl h-11" /></Field>
        <Field label={t("f_desc")}><Textarea data-testid="submit-desc" value={form.short_description} onChange={set("short_description")} rows={3} className="rounded-xl" /></Field>

        <div>
          <Label className="text-sm font-semibold text-stone-800 mb-2 block">{t("f_cats")}</Label>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button type="button" key={c} data-testid={`submit-cat-${c}`} onClick={() => toggle(c)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  selected.includes(c) ? "bg-[#2E604A] text-white border-[#2E604A]" : "bg-white text-stone-600 border-stone-300 hover:border-[#2E604A]"
                }`}>
                <CategoryIcon category={c} className="w-3.5 h-3.5" /> {catLabel(c)}
              </button>
            ))}
          </div>
        </div>

        <Field label={t("f_services")}><Textarea data-testid="submit-services" value={form.services} onChange={set("services")} rows={4} className="rounded-xl" /></Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("f_phone")}><Input data-testid="submit-phone" value={form.phone} onChange={set("phone")} className="rounded-xl h-11" /></Field>
          <Field label={t("f_email")}><Input data-testid="submit-email" value={form.email} onChange={set("email")} className="rounded-xl h-11" /></Field>
          <Field label={t("f_website")}><Input data-testid="submit-website" value={form.website} onChange={set("website")} className="rounded-xl h-11" /></Field>
          <Field label={t("f_address")}><Input data-testid="submit-address" value={form.address} onChange={set("address")} className="rounded-xl h-11" /></Field>
        </div>

        <Button type="submit" data-testid="submit-btn" disabled={submitting}
          className="w-full rounded-full bg-[#C05E4B] hover:bg-[#A65140] h-12 text-base font-semibold gap-2">
          <Send className="w-4 h-4" /> {t("f_submit")}
        </Button>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-semibold text-stone-800 mb-1.5 block">{label}</Label>
    {children}
  </div>
);
